import React from "react";
import { Box, Typography } from "@mui/material";
import {
  getM365ConnectionCheckFixInstructions,
  isM365CheckFailureStatus,
} from "../../utils/m365ConnectionCheckInstructions";

export type M365ConnectionCheckSub = {
  label: string;
  status: string;
  detail?: string;
};

export type M365ConnectionCheck = {
  id: string;
  label: string;
  status: string;
  detail?: string;
  subchecks?: M365ConnectionCheckSub[];
};

function M365ConnectionCheckFixBlock({
  checkId,
  show,
}: {
  checkId: string;
  show: boolean;
}) {
  if (!show) return null;
  const steps = getM365ConnectionCheckFixInstructions(checkId);
  if (!steps?.length) return null;
  return (
    <Box
      sx={{
        mt: 0.75,
        width: "100%",
        pl: { xs: 0, sm: "92px" },
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: "#7c6a5c",
          fontFamily: "var(--theo-font-sans)",
          mb: 0.35,
        }}
      >
        How to fix
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 0 }}>
        {steps.map((line, i) => (
          <Typography
            key={i}
            component="li"
            sx={{
              fontSize: 12,
              color: "#4b5563",
              fontFamily: "var(--theo-font-sans)",
              lineHeight: 1.5,
              mb: 0.35,
              display: "list-item",
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

const statusColor = (s: string) => {
  const t = String(s || "").toLowerCase();
  if (t === "success") return "#16a34a";
  if (t === "skipped") return "#6b7280";
  return "#dc2626";
};

const statusWord = (s: string) => {
  const t = String(s || "").toLowerCase();
  if (t === "success") return "Success";
  if (t === "skipped") return "Skipped";
  return "Failed";
};

export interface M365ConnectionChecksResultsBoxProps {
  checks: M365ConnectionCheck[];
  /** Extra styles on the outer panel (e.g. margin). */
  sx?: object;
}

/**
 * Same “Connection and permissions checks” panel as App Configuration → Test M365 Credentials and Permissions.
 */
const M365ConnectionChecksResultsBox: React.FC<M365ConnectionChecksResultsBoxProps> = ({
  checks,
  sx = {},
}) => {
  if (!checks?.length) return null;
  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        p: 2,
        borderRadius: "8px",
        border: "1px solid #E7DED4",
        backgroundColor: "#FFFCF9",
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: "#8B7355",
          mb: 0.5,
        }}
      >
        CONNECTION AND PERMISSIONS CHECKS
      </Typography>
      <Typography
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontSize: 15,
          fontWeight: 700,
          color: "#1a1a1a",
          mb: 1,
        }}
      >
        Graph, Exchange & Directory Role
      </Typography>
      <Typography
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontSize: 13,
          color: "#5A5E73",
          lineHeight: 1.45,
          mb: 1.5,
        }}
      >
        Includes Graph/Exchange API permissions and the Exchange, Compliance, Teams, and
        SharePoint Administrator directory roles (required for Exchange Online, Security &
        Compliance, Teams, and SharePoint PowerShell).
      </Typography>
      {checks.map((c) => {
        if (c.subchecks && c.subchecks.length > 0) {
          const pst = String(c.status || "").toLowerCase();
          const allSubOk = c.subchecks.every(
            (s) => String(s.status || "").toLowerCase() === "success"
          );
          const hasSubError = c.subchecks.some(
            (s) => String(s.status || "").toLowerCase() === "error"
          );
          return (
            <Box
              key={c.id}
              sx={{
                mb: 1.5,
                fontFamily: "var(--theo-font-sans)",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {allSubOk ? (
                <Typography
                  sx={{
                    color: "#1a1a1a",
                    fontWeight: 700,
                    wordBreak: "break-word",
                    mb: 0.75,
                  }}
                >
                  {c.label}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: statusColor(pst),
                      fontWeight: 700,
                      minWidth: 72,
                      flexShrink: 0,
                      textTransform: "capitalize",
                    }}
                  >
                    {statusWord(pst)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#1a1a1a",
                      fontWeight: 700,
                      wordBreak: "break-word",
                    }}
                  >
                    {c.label}
                  </Typography>
                </Box>
              )}
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 0,
                  ml: allSubOk ? { xs: 0, sm: 1.5 } : { xs: 0, sm: "92px" },
                  listStyle: "none",
                }}
              >
                {c.subchecks.map((sub, j) => {
                  const sst = String(sub.status || "").toLowerCase();
                  return (
                    <Box
                      key={`${c.id}-sub-${j}`}
                      component="li"
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        mb: 0.65,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color: statusColor(sst),
                          fontWeight: 700,
                          minWidth: 72,
                          flexShrink: 0,
                          fontSize: 12,
                          textTransform: "capitalize",
                        }}
                      >
                        {statusWord(sst)}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: "#374151",
                          wordBreak: "break-word",
                          fontSize: 13,
                        }}
                      >
                        <strong>{sub.label}</strong>
                        {sub.detail ? ` — ${sub.detail}` : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <M365ConnectionCheckFixBlock checkId={c.id} show={hasSubError} />
            </Box>
          );
        }

        const st = String(c.status || "").toLowerCase();
        return (
          <Box
            key={c.id}
            sx={{
              mb: 1,
              fontFamily: "var(--theo-font-sans)",
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: statusColor(st),
                  fontWeight: 700,
                  minWidth: 72,
                  flexShrink: 0,
                  textTransform: "capitalize",
                }}
              >
                {statusWord(st)}
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: "#374151",
                  wordBreak: "break-word",
                }}
              >
                <strong>{c.label}</strong>
                {c.detail ? ` — ${c.detail}` : ""}
              </Typography>
            </Box>
            <M365ConnectionCheckFixBlock
              checkId={c.id}
              show={isM365CheckFailureStatus(st)}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default M365ConnectionChecksResultsBox;
